import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ExternalLink, Trash2, Globe, TrendingUp, Users, Gauge, Loader2, Sparkles } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/projects"); setProjects(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try { await api.delete(`/projects/${id}`); setProjects((p) => p.filter((x) => x.id !== id)); toast.success("Project deleted."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const publishedCount = (projects || []).filter((p) => p.published).length;
  const limit = user?.plan_limit ?? 1;
  const count = projects?.length ?? 0;
  const atLimit = projects !== null && count >= limit;
  const limitLabel = limit >= 1000000 ? "∞" : limit;

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="ml-label mb-1">Dashboard</div>
            <h1 className="font-heading font-bold text-3xl tracking-tight text-[#09090b]">Welcome back, {user?.name?.split(" ")[0] || "there"}.</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono-ml text-xs uppercase px-3 py-1.5 rounded-full bg-[#09090b] text-white">{user?.plan} plan</span>
            <Link to="/pricing" data-testid="upgrade-plan-btn" className="border border-border rounded-full px-4 py-2.5 font-semibold text-sm font-body inline-flex items-center gap-2 hover:bg-[#f4f4f5]">
              <Sparkles size={15} className="text-sky-500" /> {user?.plan === "free" ? "Upgrade" : "Manage plan"}
            </Link>
            {atLimit ? (
              <Link to="/pricing" data-testid="new-project-btn" className="ml-ai-btn rounded-full px-5 py-2.5 font-semibold text-sm font-body inline-flex items-center gap-2"><Sparkles size={16} /> Upgrade for more</Link>
            ) : (
              <Link to="/" data-testid="new-project-btn" className="ml-ai-btn rounded-full px-5 py-2.5 font-semibold text-sm font-body inline-flex items-center gap-2"><Plus size={16} /> New Website</Link>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-5 mt-8">
          <Stat icon={Globe} label="Websites" value={projects === null ? "—" : `${count} / ${limitLabel}`} />
          <Stat icon={TrendingUp} label="Published" value={publishedCount} />
          <Stat icon={Users} label="Plan" value={user?.plan} isText />
        </div>

        <h2 className="font-heading font-semibold text-xl text-[#09090b] mt-12 mb-5">Your projects</h2>

        {projects === null ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-[#09090b]" /></div>
        ) : projects.length === 0 ? (
          <div className="bg-white border border-dashed border-border rounded-2xl p-16 text-center">
            <Sparkles className="mx-auto text-sky-500 mb-4" />
            <h3 className="font-heading font-semibold text-lg text-[#09090b]">No websites yet</h3>
            <p className="font-body text-sm text-[#52525b] mt-1">Paste a Google Maps URL to build your first one.</p>
            <Link to="/" className="inline-block mt-5 ml-ai-btn rounded-full px-6 py-2.5 font-semibold text-sm font-body">Create your first website</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((p) => (
              <ProjectCard key={p.id} p={p} onOpen={() => nav(`/editor/${p.id}`)} onDelete={() => del(p.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, isText }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between">
        <span className="ml-label">{label}</span>
        <Icon size={16} className="text-[#a1a1aa]" />
      </div>
      <div className={`font-heading font-bold text-[#09090b] mt-3 ${isText ? "text-xl capitalize" : "text-3xl"}`}>{value}</div>
    </div>
  );
}

function ProjectCard({ p, onOpen, onDelete }) {
  const b = p.profile?.business || {};
  const score = p.website?.meta?.conversion_score;
  return (
    <div className="bg-white border border-border rounded-2xl overflow-hidden group" data-testid={`project-${p.id}`}>
      <div className="h-32 relative flex items-center justify-center" style={{ background: p.website?.colors?.primary || "#09090b" }}>
        <span className="font-heading font-bold text-xl text-white truncate px-4">{b.name}</span>
        {p.published && <span className="absolute top-3 right-3 text-[10px] font-mono-ml uppercase bg-emerald-500 text-white px-2 py-0.5 rounded-full">Live</span>}
      </div>
      <div className="p-5">
        <div className="font-body text-sm text-[#52525b]">{b.category} · {b.city}</div>
        <div className="flex items-center gap-3 mt-3">
          {typeof score === "number" && <span className="flex items-center gap-1 text-xs font-mono-ml text-[#52525b]"><Gauge size={13} /> {score}/100</span>}
        </div>
        <div className="flex items-center gap-2 mt-4">
          <button data-testid={`open-${p.id}`} onClick={onOpen} className="flex-1 bg-[#09090b] text-white rounded-full py-2 text-sm font-semibold font-body">Edit</button>
          {p.published && (
            <a href={`/site/${p.subdomain}`} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-border flex items-center justify-center text-[#52525b]"><ExternalLink size={15} /></a>
          )}
          <button data-testid={`delete-${p.id}`} onClick={onDelete} className="w-9 h-9 rounded-full border border-border flex items-center justify-center text-[#52525b] hover:text-red-500"><Trash2 size={15} /></button>
        </div>
      </div>
    </div>
  );
}
